import { useEffect, useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { errorToast, successToast, warningToast } from '../../../../helpers/toastHelper';
import Button from '../../../Button/Button';
import { useDispatch, useSelector } from 'react-redux';
import {
  imagePaymentFormPopupActions,
  selectDisabledRenderJobId,
  selectIsAmountForRenderSufficient,
  selectOrgAvailableBalance,
  selectRemainingAmountForRender,
} from '../../../../redux/slicers/imagePaymentFormPopupSlicer';
import { useRenderPreviewPopup } from '../../../../hooks/useRenderPreviewPopup';
import { apiPostPartialPaymentApi } from '../../../../helpers/api';
import { createRenderAPI } from '../../../../api/camera/renders/renderCamera';
import {
  addPreview,
  addRender,
  selectActiveTab,
} from '../../../../redux/slicers/admin/paginatedRenderPreviewsSlicer';
import { selectPopupType } from '../../../../redux/slicers/camera/renderPreviewPoppupSlicer';
import { RP_TYPE } from '../../../../config/constant/unityConstants';
import { getAppState } from '../../../../redux/store';

export const PaymentCheckoutForm = ({ clientSecret }) => {
  const { closePopup } = useRenderPreviewPopup();
  const renderJobId = useSelector(selectDisabledRenderJobId);
  const remainingAmount = useSelector(selectRemainingAmountForRender);
  const availableBalance = useSelector(selectOrgAvailableBalance);
  const isRenderAmountSufficient = useSelector(selectIsAmountForRenderSufficient);
  const popupType = useSelector(selectPopupType);
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const isPreview = popupType === RP_TYPE.PREVIEW;
  const [timer, setTimer] = useState(null);
  const paymentElementOptions = {
    layout: 'tabs',
  };

  useEffect(() => {
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  const onReady = () => {
    setTimeout(() => {
      dispatch(imagePaymentFormPopupActions.setIsStripeLoading(false));
    }, 500);
  };

  const handleStatusMessage = (status) => {
    switch (status) {
      case 'succeeded':
        successToast('Payment succeeded. Your image is being processed.');
        closeModal();
        closePopup();
        break;
      case 'processing':
        warningToast(
          'Your payment is in processing. Once successful, your image will be send for processing.'
        );
        closeModal();
        closePopup();
        break;
      case 'requires_payment_method':
      case 'requires_action':
        errorToast('Your payment was not successful, please try again.');
        break;
      default:
        errorToast(status || 'Something went wrong, please try again.');
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setIsLoading(true);
    const response = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });
    const { error = { message: '', type: '' }, paymentIntent = { status: '' } } = response;
    const isSucceeded = paymentIntent?.status === 'succeeded';
    if (!isRenderAmountSufficient && availableBalance > 0 && isSucceeded) {
      await apiPostPartialPaymentApi(renderJobId);
    }
    if (isSucceeded) {
      await updateRenderData();
    }
    handleStatusMessage(paymentIntent?.status || error?.message);
    setIsLoading(false);
  };

  const closeModal = () => {
    dispatch(imagePaymentFormPopupActions.close());
  };

  const updateRenderData = async () => {
    await createRenderAPI(null, {
      renderId: renderJobId,
      showError: false,
      onError: () => {
        if (timer) {
          clearTimeout(timer);
        }
        const localTimer = setTimeout(() => {
          updateRenderData();
        }, 2000);
        setTimer(localTimer);
      },
      onSuccess: (data) => {
        const thumbnail = data?.images?.[0]?.thumbnail;
        if (thumbnail) {
          // download img to force cache
          const img = document.createElement('img');
          img.setAttribute('src', thumbnail);
          img.style.display = 'none';
          img.onload = () => {
            document.body.removeChild(img);
          };
          img.onerror = () => {
            document.body.removeChild(img);
          };
          document.body.appendChild(img);
        }
        const addAction = isPreview ? addPreview : addRender;
        const activeTab = selectActiveTab(getAppState());
        dispatch(addAction({ data, activeTab }));
      },
    });
  };

  if (!clientSecret) {
    return null;
  }

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" options={paymentElementOptions} onReady={onReady} />
      <Button
        id="submit"
        size="xlg"
        color="warning"
        className="w-100 mt-3"
        fullWidth
        loading={isLoading}
        type="submit"
        disabled={isLoading || !stripe || !elements}>
        Pay Now (${remainingAmount})
      </Button>
      <Button
        id="cancel"
        size="xlg"
        color="light"
        className="w-100 mt-3"
        fullWidth
        onClick={closeModal}>
        Cancel
      </Button>
    </form>
  );
};
